import { UserId } from 'features/users/types';
import { EventUser } from 'services/signaling';

export type ChatMessageId = string;

export interface ChatMessageUser extends EventUser {
  externalId?: string;
}

export interface ChatMessageReaction {
  emoji: string;
  initiator: EventUser;
  local: boolean;
}

export interface ToggleChatReactionPayload {
  messageId: ChatMessageId;
  emoji: string;
}

export interface ChatReactionToggledPayload {
  data: {
    messageId: ChatMessageId;
    emoji: string;
    added: boolean;
    local: boolean;
  };
  initiator: EventUser;
}

export interface ReactionInitiator {
  id: string;
  externalId?: string;
  userId?: UserId;
  name?: string | null;
  avatarColor?: string;
  avatarUrl?: string;
  initials?: string;
  local: boolean;
}

export interface GroupedReaction {
  emoji: string;
  initiators: ReactionInitiator[];
  localUserReacted: boolean;
}

export interface SetMessageReactionsPayload {
  messageId: ChatMessageId;
  reactions: GroupedReaction[];
}

export interface ChatMessage {
  id: ChatMessageId;
  message: string;
  date: string;
  local: boolean;
  user: ChatMessageUser;
  targetUser?: ChatMessageUser;
  privateGroup?: boolean;
  reactions?: GroupedReaction[];
  images?: ChatMessageImage[];
  optimistic?: boolean;
  reply?: boolean;
  parentId?: ChatMessageId | null;
}

export interface ChatMessagePayload extends Omit<ChatMessage, 'reactions'> {
  reactions?: ChatMessageReaction[];
}

export type PrivateChatActiveView = 'list' | 'personal';

export interface PrivateChatActiveViewState {
  name: PrivateChatActiveView;
  data: Record<string, any>;
}

export interface PrivateGroupChatState {
  id: string;
  name: string;
  roles: string[];
  avatarColor: string;
}

export interface PrivateChatState {
  chats: Record<
    string,
    {
      lastActivity: string;
      messages: ChatMessageId[];
      group?: boolean;
      externalId?: string;
    }
  >;
  activeView: PrivateChatActiveViewState;
  groupChat: PrivateGroupChatState;
}

export enum ChatTab {
  public,
  private,
}

export interface ChatState {
  panelOpen: boolean;
  activeTab: ChatTab;
  localUserId: UserId;
  messageSentWithCurrentKey: boolean;
  unreadCounters: Record<string, number>;
  readMessages: Record<ChatMessageId, boolean>;
  entities: Record<string, ChatMessage>;
  publicIds: string[];
  privateChat: PrivateChatState;
  senders: Record<UserId, ChatMessageUser>;
  localUserExternalId?: string;
  exportProcessing: boolean;
  chatReactionsExtended: boolean;
  pendingImages: PendingChatImage[];
  optimisticImageBlobs: Record<ChatMessageId, string[]>;
  imageLightbox: { open: boolean; messageId: ChatMessageId | null; index: number };
  replyToId: ChatMessageId | null;
}

export interface NewMessagePayload {
  message: string;
  targetUserId?: UserId;
  privateGroup?: boolean;
  images?: ChatImageUploadRequest[];
  parentId?: ChatMessageId;
}

export interface ChatMessageDeletedPayload {
  id: string;
  userId: UserId;
  targetUserId?: UserId;
  privateGroup?: boolean;
}

export interface ChatImageDeletedPayload {
  messageId: string;
  id: string;
}

export interface PublicChatClearedPayload {
  user: EventUser;
}

export interface PrivateChatOpenedPayload {
  id: string;
  group?: boolean;
}

export type UnreadMessageCounterUpdatedPayload = {
  key: string;
  modifier: number;
};

export interface PrivateChatView {
  id: string;
  name: string | null;
  disabled: boolean;
  messages: ChatMessage[];
  avatarColor: string;
  initials?: string;
  avatarUrl?: string;
  group?: boolean;
  targetId?: string;
}

export interface TargetChatItem extends Omit<PrivateChatView, 'name' | 'messages' | 'disabled'> {
  name: string;
  targetId: string;
}

export interface ChatImageUploadRequest {
  name: string;
  fileSize: number;
}

export interface ChatImageUploadPreparedItem {
  file_id: string;
  file_name: string;
  storage: string;
  external_storage_url: string;
  expiration_timestamp: number;
  callback_url: string;
  callback_token: string;
}

export interface ChatMessageUploadPreparedPayload {
  messageId: string;
  images: ChatImageUploadPreparedItem[];
}

export interface ChatMessageImage {
  id: string;
  url: string;
  thumbnailUrl: string;
  fileName: string;
  localId?: string;
}

export interface PendingChatImage {
  localId: string;
  file: File;
  previewUrl: string;
  name: string;
  fileSize: number;
}
